import { ProductOverviewAttribute, ProductOverviewCard } from '@app/components/products/ProductOverviewCard';
import { ProductQuantityPreview } from '@app/components/products/ProductQuantityPreview';
import { Card } from '@app/components/various/Card';
import { Status } from '@app/components/various/Status';
import { getOrderVariants } from '@app/ducks';
import { getShippedProducts } from '@app/logic/products';
import { getShippedProductsQuantity } from '@app/logic/shipment';
import { getAddressLine } from '@app/utils/getAddressLine';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Shipment } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ShipmentCardInvoicesAttribute } from './ShipmentCardInvoicesAttribute';

interface Props {
  shipment: Shipment;
}

export const CheckoutSuccessShipmentCard = ({ shipment }: Props) => {
  const { shipmentId, status, sentDate, shippingAddress, shippingMethod, trackingUrl, tracking, products, invoices = [] } = shipment;
  const { t } = useTranslation(['common', 'shipping']);
  const orderVariants = useSelector(getOrderVariants);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const addressString = React.useMemo(() => {
    return [getAddressLine(shippingAddress.address1, shippingAddress.address2), shippingAddress.city, shippingAddress.countryName]
      .filter(part => isDefined(part) && !isEmpty(part.trim()))
      .join(', ');
  }, [shippingAddress]);

  const shipmentProducts = React.useMemo(() => {
    const items = getShippedProducts(products);

    return orderVariants
      .map(product => ({
        ...product,
        shippedItems: items[`${product.product}-${product.variant}`] ?? [],
      }))
      .filter(product => isDefined(product.shippedItems));
  }, [products, orderVariants]);

  return (
    <Card.Wrapper variant="gray" key={shipmentId}>
      <Card.Header>
        <Status type={status === 'shipped' ? 'completed' : 'pending'} />
        {status === 'shipped' ? t('shipping:shipped') : t('shipping:not_shipped')}
      </Card.Header>
      <Card.Title>{t('common:shipment_number', { shipmentNumber: shipment.shipmentId })}</Card.Title>
      <Card.AttributeList>
        <Card.Attribute name={t('common:quantity')} value={getShippedProductsQuantity(shipment)} />
        <Card.Attribute name={t('common:sent')} value={sentDate} />
        <Card.Attribute name={t('common:contact_person')} value={shippingAddress.contactPerson} />
        <ShipmentCardInvoicesAttribute invoices={invoices} />
        <Card.Attribute name={t('common:address')} value={addressString} />
        <Card.Attribute name={t('common:carrier')} value={shippingMethod.carrier} />
        {isDefined(trackingUrl) ?
          <Card.AttributeLink to={trackingUrl} target="_blank" name={t('common:tracking_id')} value={tracking} />
        : <Card.Attribute name={t('common:tracking_id')} value={tracking} />}
      </Card.AttributeList>
      <Card.Expander isExpanded={isExpanded} expanderLabel="Toggle details" toggleExpanded={toggleExpanded}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {shipmentProducts.map(product => (
            <ProductOverviewCard key={product.product} product={product} variantName={product.variantName}>
              <ProductOverviewAttribute
                label={t('common:quantity')}
                value={<ProductQuantityPreview title={t('common:quantity')} items={product.shippedItems} itemTable={product.itemTable} />}
              />
            </ProductOverviewCard>
          ))}
        </div>
      </Card.Expander>
    </Card.Wrapper>
  );
};
